/*
  ATD - Cart drawer extras

  Inyecta dinámicamente:
   1. Barra de progreso de envío gratis (al inicio del drawer).
   2. Lista de productos sugeridos (upsell, al final del drawer).

  Sin modificar core de Horizon. Usa:
   - 'cart:update' (ThemeEvents.cartUpdate) para refrescar.
   - Section Rendering API para que cart-items-component refresque
     items automáticamente al agregar un upsell.
*/
(() => {
  if (customElements.get('atd-cart-extras')) return;

  const CART_DRAWER_SEL = 'cart-drawer-component';
  // Posibles selectores donde inyectar la barra (ANTES del primer match)
  const PROGRESS_BEFORE_SELECTORS = [
    '.cart-items__wrapper',
    'cart-items-component',
    '.cart-drawer__items',
    '.cart-drawer__content',
  ];
  // Inyectar el upsell DENTRO del scroll area (al final del wrapper de items),
  // para que items + upsell compartan el mismo scroll y no se separen en zonas.
  const UPSELL_PARENT_SELECTORS = [
    '.cart-items__wrapper',
    '.cart-items',
    '.cart-drawer__content',
    '.cart-drawer__inner',
    'dialog',
  ];

  class AtdCartExtras extends HTMLElement {
    connectedCallback() {
      this.config = {
        enableProgress: this.dataset.enableProgress === 'true',
        thresholdCents: Number(this.dataset.thresholdCents) || 0,
        textBelow: this.dataset.textBelow || '',
        textComplete: this.dataset.textComplete || '',
        barColorBelow: this.dataset.barColorBelow || '#dc2626',
        barColorComplete: this.dataset.barColorComplete || '#16a34a',
        barTrackColor: this.dataset.barTrackColor || '#e5e5e5',
        textColor: this.dataset.textColor || '#111',
        progressBg: this.dataset.progressBg || '#fafafa',
        enableUpsell: this.dataset.enableUpsell === 'true',
        cardLayout: this.dataset.cardLayout || 'horizontal',
        currency: this.dataset.currency || 'MXN',
      };

      this.#findDrawer().then((drawer) => {
        this.drawer = drawer;
        this.#init();
      });
    }

    async #findDrawer(maxWait = 5000) {
      const found = document.querySelector(CART_DRAWER_SEL);
      if (found) return found;
      return new Promise((resolve) => {
        const obs = new MutationObserver(() => {
          const node = document.querySelector(CART_DRAWER_SEL);
          if (node) {
            obs.disconnect();
            resolve(node);
          }
        });
        obs.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => {
          obs.disconnect();
          resolve(document.querySelector(CART_DRAWER_SEL));
        }, maxWait);
      });
    }

    #init() {
      if (!this.drawer) return;

      this.refresh();

      this._onCartUpdate = () => {
        // Doble refresh: inmediato + diferido (cubre el morphSection de Horizon
        // que borra los nodos que inyectamos cuando el cart cambia).
        this.refresh();
        setTimeout(() => this.refresh(), 250);
      };
      document.addEventListener('cart:update', this._onCartUpdate);

      // Refresh adicional al abrir el drawer (cubre cambios externos)
      this._onDialogOpen = () => this.refresh();
      this.drawer.addEventListener('dialog:open', this._onDialogOpen);

      // MutationObserver: si Horizon re-renderiza y borra nuestros nodos,
      // los volvemos a inyectar.
      this._mutationObs = new MutationObserver(() => {
        const hasProgress = this.config.enableProgress
          ? !!this.drawer.querySelector('[data-atd-cart-progress]')
          : true;
        const hasUpsell = this.config.enableUpsell
          ? !!this.drawer.querySelector('[data-atd-cart-upsell]')
          : true;
        if (!hasProgress || !hasUpsell) {
          this.refresh();
        }
      });
      this._mutationObs.observe(this.drawer, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      if (this._onCartUpdate) document.removeEventListener('cart:update', this._onCartUpdate);
      if (this._onDialogOpen && this.drawer) {
        this.drawer.removeEventListener('dialog:open', this._onDialogOpen);
      }
      if (this._mutationObs) this._mutationObs.disconnect();
    }

    async refresh() {
      try {
        const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
        if (this.config.enableProgress) {
          this.#renderProgress(cart);
        } else {
          this.#removeNode('[data-atd-cart-progress]');
        }
        if (this.config.enableUpsell) {
          this.#renderUpsell(cart);
        } else {
          this.#removeNode('[data-atd-cart-upsell]');
        }
      } catch (e) {
        console.error('[atd-cart-extras] refresh failed', e);
      }
    }

    #removeNode(selector) {
      this.drawer?.querySelectorAll(selector).forEach((n) => n.remove());
    }

    // ---------- Barra de progreso ----------
    #renderProgress(cart) {
      const goal = this.config.thresholdCents;
      const total = cart.total_price;
      const remaining = Math.max(0, goal - total);
      const percent = goal > 0 ? Math.min(100, (total / goal) * 100) : 0;

      let host = this.drawer.querySelector('[data-atd-cart-progress]');
      if (!host) {
        const tpl = this.querySelector('[data-progress-template]');
        if (!tpl) return;
        host = tpl.content.firstElementChild.cloneNode(true);
        this.#injectBefore(host, PROGRESS_BEFORE_SELECTORS);
        host.style.setProperty('--atd-cart-extras-bar-track-color', this.config.barTrackColor);
        host.style.setProperty('--atd-cart-extras-text-color', this.config.textColor);
        host.style.setProperty('--atd-cart-extras-progress-bg', this.config.progressBg);
      }

      const txt = host.querySelector('[data-atd-cart-progress-text]');
      const bar = host.querySelector('[data-atd-cart-progress-bar]');
      if (!txt || !bar) return;

      // Cambia el color según estado
      const activeColor = remaining <= 0 ? this.config.barColorComplete : this.config.barColorBelow;
      host.style.setProperty('--atd-cart-extras-bar-color', activeColor);

      if (remaining <= 0) {
        txt.textContent = this.config.textComplete;
        bar.style.width = '100%';
      } else {
        const formatted = this.#formatMoney(remaining);
        txt.textContent = (this.config.textBelow || '').replace(/\[amount\]/g, formatted);
        bar.style.width = `${percent}%`;
      }
    }

    // ---------- Upsell ----------
    #renderUpsell(cart) {
      // No mostrar upsell en estado de carrito vacío: el CTA principal del
      // empty state es "Seguir comprando" y el drawer no tiene los wrappers
      // donde el upsell se ve bien (la card pierde sus estilos).
      if (!cart.items || cart.items.length === 0) {
        this.#removeNode('[data-atd-cart-upsell]');
        return;
      }

      let host = this.drawer.querySelector('[data-atd-cart-upsell]');
      if (!host) {
        const tpl = this.querySelector('[data-upsell-template]');
        if (!tpl || !tpl.content.firstElementChild) {
          console.warn('[atd-cart-extras] upsell template vacío. ¿Configuraste productos en la sección?');
          return;
        }
        const candidate = tpl.content.firstElementChild.cloneNode(true);
        const cards = candidate.querySelectorAll('[data-product-id]');
        if (cards.length === 0) return;
        host = candidate;
        // Insertar al final del scroll area de items para que el cart drawer
        // mantenga UN solo scroll (items + upsell juntos).
        this.#injectAtEnd(host, UPSELL_PARENT_SELECTORS);
        host.querySelectorAll('[data-atd-cart-upsell-add]').forEach((btn) => {
          btn.addEventListener('click', (e) => this.#handleAdd(e));
        });
        this.#initCarouselNav(host);
      }

      const cartProductIds = new Set((cart.items || []).map((i) => String(i.product_id)));
      let visible = 0;
      const cards = host.querySelectorAll('[data-product-id]');
      cards.forEach((card) => {
        const inCart = cartProductIds.has(card.dataset.productId);
        card.hidden = inCart;
        if (!inCart) visible++;
      });

      host.hidden = visible === 0;
      this.#updateNavState(host);
    }

    #initCarouselNav(host) {
      if (host.dataset.layout !== 'carousel') return;
      const list = host.querySelector('[data-atd-cart-upsell-list]');
      const prev = host.querySelector('[data-atd-cart-upsell-prev]');
      const next = host.querySelector('[data-atd-cart-upsell-next]');
      if (!list || !prev || !next) return;

      const getVisibleCards = () =>
        Array.from(list.querySelectorAll('[data-product-id]:not([hidden])'));

      const scrollToIndex = (idx) => {
        const cards = getVisibleCards();
        if (!cards.length) return;
        const target = cards[Math.max(0, Math.min(cards.length - 1, idx))];
        const left = target.offsetLeft;
        list.scrollTo({ left, behavior: 'smooth' });
      };

      const currentIndex = () => {
        const cards = getVisibleCards();
        if (!cards.length) return 0;
        const cardWidth = cards[0].getBoundingClientRect().width || 1;
        return Math.round(list.scrollLeft / cardWidth);
      };

      prev.addEventListener('click', () => scrollToIndex(currentIndex() - 1));
      next.addEventListener('click', () => scrollToIndex(currentIndex() + 1));

      list.addEventListener('scroll', () => {
        clearTimeout(this._navStateTimer);
        this._navStateTimer = setTimeout(() => this.#updateNavState(host), 80);
      });
    }

    #updateNavState(host) {
      if (host.dataset.layout !== 'carousel') return;
      const list = host.querySelector('[data-atd-cart-upsell-list]');
      const prev = host.querySelector('[data-atd-cart-upsell-prev]');
      const next = host.querySelector('[data-atd-cart-upsell-next]');
      if (!list || !prev || !next) return;

      const visible = list.querySelectorAll('[data-product-id]:not([hidden])');
      if (visible.length <= 1) {
        host.dataset.singleCard = '';
        return;
      }
      delete host.dataset.singleCard;

      const cardWidth = visible[0].getBoundingClientRect().width || 1;
      const idx = Math.round(list.scrollLeft / cardWidth);
      const atStart = idx <= 0;
      const atEnd = idx >= visible.length - 1;

      prev.toggleAttribute('disabled', atStart);
      prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      next.toggleAttribute('disabled', atEnd);
      next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }

    async #handleAdd(e) {
      const btn = e.currentTarget;
      const variantId = btn.dataset.variantId;
      if (!variantId || btn.disabled) return;

      btn.disabled = true;

      try {
        // Encontrar sectionId del cart-items dentro del drawer para Section Rendering API
        const cartItems = this.drawer.querySelector('cart-items-component[data-section-id]')
          || this.drawer.querySelector('cart-items-component');
        const sectionId =
          cartItems?.dataset.sectionId ||
          cartItems?.getAttribute('data-section-id') ||
          cartItems?.getAttribute('section-id') ||
          null;

        const formData = new FormData();
        formData.set('id', variantId);
        formData.set('quantity', '1');
        if (sectionId) formData.set('sections', sectionId);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: formData,
          credentials: 'same-origin',
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          console.error('[atd-cart-extras] add failed', errBody);
          return;
        }

        const data = await response.json();

        // Disparar cart:update con la forma que cart-items-component espera
        const detail = {
          resource: data,
          sourceId: 'atd-cart-upsell',
          data: {
            source: 'atd-cart-upsell',
            sections: data.sections || {},
            productId: btn.closest('[data-product-id]')?.dataset.productId,
            variantId,
            itemCount: 1,
            didError: false,
          },
        };
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail }));

        // Asegurar que el drawer esté abierto
        if (typeof this.drawer.open === 'function') this.drawer.open();

        // Refresh inmediato de barra y upsell
        await this.refresh();
      } catch (err) {
        console.error('[atd-cart-extras] add error', err);
      } finally {
        btn.disabled = false;
      }
    }

    // ---------- Helpers DOM ----------
    #injectBefore(node, selectors) {
      for (const sel of selectors) {
        const target = this.drawer.querySelector(sel);
        if (target) {
          target.parentNode.insertBefore(node, target);
          return;
        }
      }
      // Fallback: prepend al drawer
      const dialog = this.drawer.querySelector('dialog') || this.drawer;
      dialog.prepend(node);
    }

    #injectAtEnd(node, selectors) {
      for (const sel of selectors) {
        const target = this.drawer.querySelector(sel);
        if (target) {
          target.appendChild(node);
          return;
        }
      }
      const dialog = this.drawer.querySelector('dialog') || this.drawer;
      dialog.appendChild(node);
    }

    // ---------- Helpers ----------
    #formatMoney(cents) {
      const value = cents / 100;
      try {
        return new Intl.NumberFormat('es-MX', {
          style: 'currency',
          currency: this.config.currency || 'MXN',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(value);
      } catch (e) {
        return `$${value.toFixed(2)}`;
      }
    }
  }

  customElements.define('atd-cart-extras', AtdCartExtras);
})();
